import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Shield } from 'lucide-react';
import { Buton, Etiket, Girdi, Iskelet, Kart, KartIcerik, Rozet, toast } from '@emanetx/ui';
import { odemeAyar, type AkbankAyar } from '@emanetx/sdk';
import { istemci } from '../istemci.js';

interface AkbankForm {
  merchantSafeId: string;
  terminalSafeId: string;
  secretKey: string;
  prod: boolean;
}

const BOS: AkbankForm = {
  merchantSafeId: '',
  terminalSafeId: '',
  secretKey: '',
  prod: false,
};

export function SayfaAkbank() {
  const qc = useQueryClient();
  const [form, setForm] = useState<AkbankForm>(BOS);

  const ayarSorgu = useQuery({
    queryKey: ['odeme-ayar', 'akbank'],
    queryFn: () => odemeAyar.akbankGetir(istemci),
  });

  useEffect(() => {
    if (ayarSorgu.data) {
      setForm({
        merchantSafeId: ayarSorgu.data.merchantSafeId,
        terminalSafeId: ayarSorgu.data.terminalSafeId,
        secretKey: '',
        prod: ayarSorgu.data.prod,
      });
    }
  }, [ayarSorgu.data]);

  const kaydet = useMutation({
    mutationFn: () => {
      const giris: Partial<AkbankAyar> = {
        merchantSafeId: form.merchantSafeId,
        terminalSafeId: form.terminalSafeId,
        prod: form.prod,
      };
      if (form.secretKey.length > 0) giris.secretKey = form.secretKey;
      return odemeAyar.akbankKaydet(istemci, giris);
    },
    onSuccess: () => {
      toast.success('Akbank ayarları kaydedildi');
      setForm((f) => ({ ...f, secretKey: '' }));
      void qc.invalidateQueries({ queryKey: ['odeme-ayar', 'akbank'] });
    },
    onError: (err: unknown) => toast.error(hataMesaji(err)),
  });

  const test = useMutation({
    mutationFn: () => odemeAyar.akbankTest(istemci),
    onSuccess: (sonuc) => {
      if (sonuc.basarili) toast.success('Akbank bağlantı testi başarılı');
      else toast.error(`Test başarısız: ${sonuc.hata ?? sonuc.saglayiciHataKodu ?? 'bilinmeyen'}`);
    },
    onError: (err: unknown) => toast.error(hataMesaji(err)),
  });

  if (ayarSorgu.isLoading) return <Iskelet className="h-80" />;

  const merchantGecerli = form.merchantSafeId.length === 32;
  const terminalGecerli = form.terminalSafeId.length === 32;
  const secretGecerli = form.secretKey.length === 0 || form.secretKey.length >= 32;
  const formGecerli =
    merchantGecerli &&
    terminalGecerli &&
    secretGecerli &&
    (ayarSorgu.data?.yapilandirildi || form.secretKey.length >= 32);

  // Form alanlari DB'deki sorgu sonucundan farkliysa kaydedilmemis degisiklik var
  const kaydedilmemisVar =
    !!ayarSorgu.data &&
    (form.merchantSafeId !== ayarSorgu.data.merchantSafeId ||
      form.terminalSafeId !== ayarSorgu.data.terminalSafeId ||
      form.prod !== ayarSorgu.data.prod ||
      form.secretKey.length > 0);

  return (
    <div className="space-y-5">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Akbank Sanal POS</h1>
          <p className="text-fume-500 dark:text-fume-400 mt-1 text-sm">
            3D_PAY_HOSTING ile tek seferlik kart ödemeleri. Cüzdan yükleme ve kiralama ödemesinde
            kullanılır.
          </p>
        </div>
        {ayarSorgu.data?.yapilandirildi && (
          <Rozet ton={form.prod ? 'kirmizi' : 'turuncu'}>{form.prod ? 'CANLI' : 'TEST'}</Rozet>
        )}
      </header>

      <Kart>
        <KartIcerik className="space-y-4">
          <div>
            <Etiket>Merchant Safe ID (32 karakter)</Etiket>
            <Girdi
              value={form.merchantSafeId}
              onChange={(e) => setForm({ ...form, merchantSafeId: e.target.value.trim() })}
              placeholder="32 karakterli merchant ID"
              maxLength={32}
              className="font-mono"
            />
            {form.merchantSafeId.length > 0 && !merchantGecerli && (
              <p className="text-kirmizi-600 mt-1 text-xs">
                Tam 32 karakter olmalı (şu an: {form.merchantSafeId.length})
              </p>
            )}
          </div>

          <div>
            <Etiket>Terminal Safe ID (32 karakter)</Etiket>
            <Girdi
              value={form.terminalSafeId}
              onChange={(e) => setForm({ ...form, terminalSafeId: e.target.value.trim() })}
              placeholder="32 karakterli terminal ID"
              maxLength={32}
              className="font-mono"
            />
            {form.terminalSafeId.length > 0 && !terminalGecerli && (
              <p className="text-kirmizi-600 mt-1 text-xs">
                Tam 32 karakter olmalı (şu an: {form.terminalSafeId.length})
              </p>
            )}
          </div>

          <div>
            <Etiket>
              Secret Key (min 32 karakter)
              {ayarSorgu.data?.yapilandirildi ? ' — değiştirmek istemiyorsan boş bırak' : ''}
            </Etiket>
            <Girdi
              type="password"
              value={form.secretKey}
              onChange={(e) => setForm({ ...form, secretKey: e.target.value.trim() })}
              placeholder={
                ayarSorgu.data?.yapilandirildi
                  ? '••••••••'
                  : 'Portal Yönetim → Güvenlik Anahtarları'
              }
              className="font-mono"
            />
            {form.secretKey.length > 0 && !secretGecerli && (
              <p className="text-kirmizi-600 mt-1 text-xs">
                En az 32 karakter (şu an: {form.secretKey.length})
              </p>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.prod}
              onChange={(e) => setForm({ ...form, prod: e.target.checked })}
              className="h-4 w-4"
            />
            <span>
              <strong>Canlı (prod)</strong> ortamı kullan — kapalıysa preprod test endpoint'leri
            </span>
          </label>

          {/* Kaydedilmemis degisiklik uyarisi — test butonu DB'deki eski creds ile
              calisir, oncesinde kaydetmek gerek (Frontend-Yuksek-1). */}
          {kaydedilmemisVar && (
            <p className="bg-turuncu-50 text-turuncu-700 dark:bg-turuncu-500/10 dark:text-turuncu-300 rounded-md px-3 py-2 text-xs">
              Kaydedilmemiş değişikliklerin var. "Bağlantıyı Test Et" mevcut kayıtlı ayarlarla
              çalışır — önce <strong>Kaydet</strong>'e bas, sonra test et.
            </p>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <Buton
              stil="cizgili"
              onClick={() => test.mutate()}
              disabled={!ayarSorgu.data?.yapilandirildi || test.isPending}
            >
              <Shield size={14} /> {test.isPending ? 'Test ediliyor...' : 'Bağlantıyı Test Et'}
            </Buton>
            <Buton onClick={() => kaydet.mutate()} disabled={!formGecerli || kaydet.isPending}>
              <CreditCard size={14} /> {kaydet.isPending ? 'Kaydediliyor...' : 'Kaydet'}
            </Buton>
          </div>
        </KartIcerik>
      </Kart>

      <Kart>
        <KartIcerik className="space-y-2 text-sm">
          <h3 className="font-semibold">Notlar</h3>
          <ul className="text-fume-500 list-inside list-disc space-y-1">
            <li>secretKey portaldan alınır: Yönetim → Güvenlik Anahtarları</li>
            <li>
              Test ortamında <code className="font-mono">prod=false</code> bırakın — preprod
              endpoint'leri (https://virtualpospaymentgatewaypre.akbank.com) kullanılır
            </li>
            <li>
              Bağlantı testi <code>VPS-XXXX</code> kodu döndüğü sürece başarılı sayılır (kimlik
              kabul edildi). Sadece HTTP 401 hash/credential sorunudur.
            </li>
            <li>
              Kart kaydetme YOK — her ödeme tek seferlik. Müşteri Akbank hosted page'inde kart
              girer, biz sadece sonucu alıp doğrularız.
            </li>
          </ul>
        </KartIcerik>
      </Kart>
    </div>
  );
}

function hataMesaji(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    return String((err as { message: string }).message);
  }
  return 'Bir hata oluştu';
}
